import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { Search, Loader2, BookMarked } from "lucide-react";

export default function CostCatalog() {
  const [division, setDivision] = useState("");
  const [q, setQ] = useState("");

  const { data: divisions = [] } = useQuery({ queryKey: ["divisions"], queryFn: async () => (await api.get("/catalog/divisions")).data });
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["catalog", division, q],
    queryFn: async () => (await api.get("/catalog", { params: { division: division || undefined, q: q || undefined } })).data,
  });

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Cost Catalog</h1>
        <p className="text-sm text-muted-foreground mt-1">Benchmarked US CSI unit rates — {items.length} items.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input data-testid="catalog-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search description or code…"
            className="w-full rounded-lg border border-input bg-card pl-10 pr-3 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
        </div>
        <select data-testid="catalog-division-select" value={division} onChange={(e) => setDivision(e.target.value)}
          className="rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary sm:w-64">
          <option value="">All divisions</option>
          {divisions.map((d) => <option key={d.code} value={d.code}>{`${d.code} — ${d.name}`}</option>)}
        </select>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {isLoading ? (
          <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground"><BookMarked className="h-8 w-8 mx-auto mb-2" />No items found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="catalog-table">
              <thead className="bg-popover sticky top-0">
                <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Division</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Unit</th>
                  <th className="px-4 py-3 text-right">Labor</th>
                  <th className="px-4 py-3 text-right">Material</th>
                  <th className="px-4 py-3 text-right">Equip.</th>
                  <th className="px-4 py-3 text-right">Unit Rate</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c, i) => (
                  <tr key={c.id} data-testid={`catalog-row-${c.id}`} className={`border-b border-border/60 hover:bg-secondary/50 transition-colors ${i % 2 ? "bg-secondary/20" : ""}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-primary">{c.code}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{c.division} {c.division_name}</td>
                    <td className="px-4 py-2.5 font-medium">{c.description}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{c.unit}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums">{money(c.labor_rate)}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums">{money(c.material_rate)}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums">{money(c.equipment_rate)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold tabular-nums text-primary">{money(c.unit_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
